import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

/**
 * Tabele z danymi produkcyjnymi maszyn — zapisywane z zewnątrz (integracja
 * sprzętowa poza tym repo), bez kolumny device_id. Jedna tabela = jeden typ
 * maszyny; filtrujemy wyłącznie po id_configuration.
 */
export const PRODUCTION_SOURCES = {
  cutting_machine_logs: { table: 'cutting_machine_logs', valueColumn: 'val_5', timestampColumn: 'created_at', label: 'Cięcie' },
  vector1: { table: 'vector1', valueColumn: 'val_1', timestampColumn: 'created_at', label: 'Vector EPAL' },
  vector2: { table: 'vector2', valueColumn: 'val_1', timestampColumn: 'created_at', label: 'Vector Mały' },
};

/**
 * Jednostka i precyzja wyświetlania ilości dla danego production_source — Cięcie
 * liczy m3 z dwoma miejscami po przecinku, Vectory liczą sztuki (palety), więc
 * w liczbach całkowitych. Używane zamiast kolumny devices.unit z bazy, bo ta
 * bywa błędnie skonfigurowana dla Vectorów.
 */
export const PRODUCTION_DISPLAY = {
  cutting_machine_logs: { unit: 'm3', decimals: 2 },
  vector1: { unit: 'szt.', decimals: 0 },
  vector2: { unit: 'szt.', decimals: 0 },
};

/**
 * Pobiera wszystkie wiersze zapytania, obchodząc domyślny limit wierszy PostgREST/Supabase.
 */
export async function fetchAllRows(query, pageSize = 1000) {
  let all = [];
  let from = 0;
  while (true) {
    const { data, error } = await query.range(from, from + pageSize - 1);
    if (error) {
      console.log('Błąd zapytania:', error);
      return null;
    }
    all = all.concat(data);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

/**
 * Pobiera logi produkcji dla danego źródła (klucz z PRODUCTION_SOURCES), dla danej
 * konfiguracji (id_configuration), w zakresie dat (proste stringi 'YYYY-MM-DD').
 */
export async function fetchProductionLogs(supabase, sourceKey, idConfiguration, fromDateStr, toDateStr) {
  const source = PRODUCTION_SOURCES[sourceKey];
  if (!source) return [];

  const query = supabase
    .from(source.table)
    .select(`${source.timestampColumn},${source.valueColumn}`)
    .eq('id_configuration', idConfiguration)
    .gte(source.timestampColumn, fromDateStr)
    .lte(source.timestampColumn, `${toDateStr}T23:59:59`)
    .order(source.timestampColumn, { ascending: true });

  const rows = await fetchAllRows(query);
  return rows || [];
}

/**
 * Suma wartości produkcji dla danego dnia.
 */
export function sumQuantityForDay(logs, valueColumn, timestampColumn, dateStr) {
  return logs
    .filter((log) => dayjs(log[timestampColumn]).format('YYYY-MM-DD') === dateStr)
    .reduce((sum, log) => sum + (log[valueColumn] || 0), 0);
}

/**
 * Średnia produkcja na godzinę: grupuje logi w kubełki pełnych godzin zegarowych
 * (np. 10:00-11:00 danego dnia), sumuje produkcję w każdym kubełku, i dzieli sumę
 * tych godzinowych sum przez liczbę godzin, w których cokolwiek wyprodukowano —
 * np. produkcja 2,5 / 3,5 / 1 w trzech godzinach daje średnią (2,5+3,5+1)/3.
 * Godziny bez żadnego logu nie wchodzą do liczby godzin w mianowniku.
 */
export function computeAveragePerHour(logs, valueColumn, timestampColumn) {
  const byHour = {};
  logs.forEach((log) => {
    const hourKey = dayjs(log[timestampColumn]).format('YYYY-MM-DD HH');
    byHour[hourKey] = (byHour[hourKey] || 0) + (log[valueColumn] || 0);
  });

  const hourlyTotals = Object.values(byHour);
  if (hourlyTotals.length === 0) return null;

  const totalQuantity = hourlyTotals.reduce((sum, qty) => sum + qty, 0);
  return totalQuantity / hourlyTotals.length;
}

/**
 * Czas pracy w minutach: dla dnia bieżącego to `now - pierwszy wpis dnia`,
 * dla dni historycznych `ostatni wpis dnia - pierwszy wpis dnia` (bo "teraz"
 * nie ma sensu dla przeszłości).
 */
export function computeWorkMinutes(logs, timestampColumn, dateStr, now, isToday) {
  const dayLogs = logs.filter((log) => dayjs(log[timestampColumn]).format('YYYY-MM-DD') === dateStr);
  if (dayLogs.length === 0) return 0;

  const times = dayLogs.map((log) => dayjs(log[timestampColumn]));
  const first = times.reduce((a, b) => (a.isBefore(b) ? a : b));
  const last = times.reduce((a, b) => (a.isAfter(b) ? a : b));
  const end = isToday ? dayjs(now) : last;

  return Math.max(0, end.diff(first, 'minute', true));
}

/**
 * Status progów premiowych (device_bonus_thresholds: name, value_from, value_to, coefficient)
 * względem aktualnej produkcji. Zwraca próg już osiągnięty (jeśli jest), najbliższy
 * nieosiągnięty próg, oraz gotowy komunikat do wyświetlenia.
 */
export function evaluateBonusThresholds(quantity, thresholds) {
  const sorted = [...(thresholds || [])].sort((a, b) => (a.value_from ?? 0) - (b.value_from ?? 0));
  if (sorted.length === 0) {
    return { reached: null, next: null, message: null };
  }

  const reached = [...sorted].reverse().find(
    (t) => quantity >= t.value_from && (t.value_to == null || quantity <= t.value_to)
  ) || null;
  const next = sorted.find((t) => quantity < t.value_from) || null;

  // Uwaga: celowo nie pokazujemy tu wysokości mnożnika (coefficient) — na
  // widoku pracownika mogłoby to zostać mylnie odczytane jako przelicznik
  // wynagrodzenia, a to zupełnie inna wartość.
  let message = null;
  if (!reached && quantity < sorted[0].value_from) {
    message = 'Próg premiowy jeszcze nieosiągnięty';
  } else if (reached) {
    message = `Próg ${reached.name} osiągnięty`;
  }

  return { reached, next, message };
}

/**
 * Paruje wpisy device_attendance (START_WORK/STOP_WORK, posortowane rosnąco po
 * timestamp) w sesje pracy na maszynie. Toleruje dwa START_WORK z rzędu (flushuje
 * poprzedni jako otwarty) oraz osierocony STOP_WORK (ignorowany). Wydzielone z
 * AttendanceEditModal.js — jedna implementacja dla obu miejsc, które jej używają.
 */
export function buildDeviceSessions(logs) {
  const sessions = [];
  let currentStart = null;

  const flushOpen = (start) => sessions.push({
    id: `open-${start.id}`,
    startId: start.id,
    stopId: null,
    deviceId: start.device_id,
    standId: start.stand_id,
    deviceName: start.devices?.name || 'Maszyna',
    standName: start.device_stands?.name || '—',
    startTime: start.timestamp,
    endTime: null,
    isOpen: true,
  });

  logs.forEach((log) => {
    if (log.type === 'START_WORK') {
      if (currentStart) flushOpen(currentStart);
      currentStart = log;
    } else if (log.type === 'STOP_WORK' && currentStart) {
      sessions.push({
        id: `${currentStart.id}-${log.id}`,
        startId: currentStart.id,
        stopId: log.id,
        deviceId: currentStart.device_id,
        standId: currentStart.stand_id,
        deviceName: currentStart.devices?.name || 'Maszyna',
        standName: currentStart.device_stands?.name || '—',
        startTime: currentStart.timestamp,
        endTime: log.timestamp,
        isOpen: false,
      });
      currentStart = null;
    }
  });

  if (currentStart) flushOpen(currentStart);

  return sessions;
}

/**
 * Sesje na maszynach z policzoną produkcją i dodatkiem premiowym, dla dowolnego
 * zakresu pracowników/maszyn w danej konfiguracji: sesje -> produkcja w oknie
 * każdej sesji -> suma produkcji (pracownik, maszyna) w całym tygodniu ISO ->
 * próg premiowy tej sumy -> wartość dodatku (jednostki nad normą TYGODNIOWĄ
 * maszyny × bonus_rate pracownika na danym stanowisku × coefficient progu).
 * Premia nie jest już wielkością "per sesja/dzień" — każda sesja jest tylko
 * anotowana wynikiem całego tygodnia, do którego należy (patrz weekly* pola).
 * Sesje otwarte (bez STOP_WORK) trafiają do wyniku z quantity=null — nie da się
 * policzyć produkcji bez godziny zakończenia (ale wciąż liczą się do tygodnia
 * przez inne, zamknięte sesje tego samego pracownika/maszyny). `employeeId`/
 * `deviceId` zawężają wynik do jednego pracownika/maszyny; pominięte — obejmują
 * wszystkich/wszystkie.
 *
 * Zwraca sesje z całego zakresu dat rozszerzonego do pełnych tygodni ISO
 * (nieprzyciętych do `dateFrom`/`dateTo`) — przycinanie do żądanego okresu
 * robią dopiero konsumenci (`computeProductionSessions`), żeby tygodnie na
 * granicy okresu miały poprawną sumę tygodniową liczoną z kompletnych danych.
 */
async function fetchAnnotatedSessions(supabase, { idConfiguration, dateFrom, dateTo, employeeId = null, deviceId = null }) {
  const fetchFrom = dayjs(dateFrom).startOf('isoWeek').format('YYYY-MM-DD');
  const fetchTo = dayjs(dateTo).endOf('isoWeek').format('YYYY-MM-DD');

  let query = supabase
    .from('device_attendance')
    .select('*, employees(id, name, surname), devices(id, name, daily_target, unit, production_source), device_stands(id, name)')
    .eq('id_configuration', idConfiguration)
    .gte('timestamp', `${fetchFrom}T00:00:00`)
    .lte('timestamp', `${fetchTo}T23:59:59`)
    .order('timestamp', { ascending: true });
  if (employeeId) query = query.eq('employee_id', employeeId);
  if (deviceId) query = query.eq('device_id', deviceId);

  const { data: logs } = await query;
  if (!logs || logs.length === 0) return [];

  // Grupowanie po (attendance_id, employee_id) — jedna zmiana jednego pracownika to
  // jedna grupa, żeby sesje różnych osób/zmian nigdy się nie mieszały przy parowaniu
  // START/STOP.
  const byGroup = {};
  logs.forEach((log) => {
    const key = `${log.attendance_id ?? 'none'}|${log.employee_id}`;
    (byGroup[key] = byGroup[key] || []).push(log);
  });
  const sessions = Object.values(byGroup).flatMap((group) =>
    buildDeviceSessions(group).map((s) => ({
      ...s,
      employeeId: group[0].employee_id,
      employeeName: group[0].employees ? `${group[0].employees.name} ${group[0].employees.surname}` : 'Nieznany',
    }))
  );
  if (sessions.length === 0) return [];

  const employeeIds = [...new Set(sessions.map((s) => s.employeeId))];
  const { data: standRates } = await supabase
    .from('employee_device_stands')
    .select('employee_id, device_stand_id, bonus_rate')
    .in('employee_id', employeeIds);
  const rateByEmployeeStand = {};
  (standRates || []).forEach((r) => { rateByEmployeeStand[`${r.employee_id}|${r.device_stand_id}`] = r.bonus_rate; });

  const deviceIds = [...new Set(sessions.map((s) => s.deviceId))];
  const { data: thresholdsData } = await supabase
    .from('device_bonus_thresholds')
    .select('id, device_id, name, value_from, value_to, coefficient')
    .in('device_id', deviceIds)
    .eq('id_configuration', idConfiguration);
  const thresholdsByDevice = {};
  (thresholdsData || []).forEach((t) => {
    (thresholdsByDevice[t.device_id] = thresholdsByDevice[t.device_id] || []).push(t);
  });

  const deviceInfoById = {};
  sessions.forEach((s) => {
    if (!deviceInfoById[s.deviceId]) {
      const log = logs.find((l) => l.device_id === s.deviceId);
      if (log?.devices) deviceInfoById[s.deviceId] = log.devices;
    }
  });

  // Grupowanie po production_source (nie po deviceId) — jeśli kilka urządzeń
  // dzieli tę samą tabelę źródłową (np. dwa stanowiska na jednym Vectorze), i
  // tak zwróciłyby identyczne dane (fetchProductionLogs filtruje wyłącznie po
  // id_configuration), więc pobieramy ją raz i współdzielimy wynik.
  const deviceIdsBySource = {};
  Object.entries(deviceInfoById).forEach(([devId, dev]) => {
    if (!dev.production_source) return;
    (deviceIdsBySource[dev.production_source] = deviceIdsBySource[dev.production_source] || []).push(devId);
  });

  const logsBySource = {};
  await Promise.all(
    Object.keys(deviceIdsBySource).map(async (sourceKey) => {
      logsBySource[sourceKey] = await fetchProductionLogs(
        supabase, sourceKey, idConfiguration, fetchFrom, fetchTo
      );
    })
  );

  const productionLogsByDevice = {};
  Object.entries(deviceIdsBySource).forEach(([sourceKey, devIds]) => {
    devIds.forEach((devId) => { productionLogsByDevice[devId] = logsBySource[sourceKey]; });
  });

  // Rozdział produkcji logów między sesje NA POZIOMIE URZĄDZENIA (nie per sesja z osobna):
  // jeśli dwóch (albo więcej) pracowników ma na tej samej maszynie nakładające się w
  // czasie sesje (np. dwóch jednocześnie na jednym Vectorze), ten sam surowy log nie może
  // zostać przypisany w całości każdemu z nich — to podwaja/trojuje wykazaną produkcję.
  // Zamiast tego każdy log dzielimy po równo między wszystkie sesje aktywne w jego
  // momencie, więc suma po sesjach urządzenia == suma surowych logów (bez inflacji),
  // a przy jednym aktywnym pracowniku zachowanie jest identyczne jak wcześniej (100%).
  const closedSessionsByDevice = {};
  sessions.forEach((s) => {
    if (!s.isOpen) {
      (closedSessionsByDevice[s.deviceId] = closedSessionsByDevice[s.deviceId] || []).push({
        id: s.id, start: dayjs(s.startTime), end: dayjs(s.endTime),
      });
    }
  });

  const quantityBySessionId = {};
  Object.entries(closedSessionsByDevice).forEach(([deviceId, devSessions]) => {
    const device = deviceInfoById[deviceId] || {};
    const source = device.production_source ? PRODUCTION_SOURCES[device.production_source] : null;
    if (!source) return;
    const prodLogs = productionLogsByDevice[deviceId] || [];
    prodLogs.forEach((log) => {
      const t = dayjs(log[source.timestampColumn]);
      const active = devSessions.filter((ds) => !t.isBefore(ds.start) && !t.isAfter(ds.end));
      if (active.length === 0) return;
      const share = (log[source.valueColumn] || 0) / active.length;
      active.forEach((ds) => {
        quantityBySessionId[ds.id] = (quantityBySessionId[ds.id] || 0) + share;
      });
    });
  });

  // Pierwszy przebieg: produkcja per sesja (bez premii — ta zależy od sumy
  // całego tygodnia, policzonej w drugim przebiegu poniżej).
  const withQuantity = sessions.map((s) => {
    const device = deviceInfoById[s.deviceId] || {};
    const bonusRate = rateByEmployeeStand[`${s.employeeId}|${s.standId}`] ?? null;
    const weeklyTarget = device.daily_target != null ? Number(device.daily_target) : null;
    const quantity = s.isOpen ? null : (quantityBySessionId[s.id] ?? 0);

    const weekStart = dayjs(s.startTime).startOf('isoWeek');
    const weekKey = `${weekStart.isoWeekYear()}-W${String(weekStart.isoWeek()).padStart(2, '0')}`;

    return {
      id: s.id,
      date: s.startTime,
      employeeId: s.employeeId,
      employeeName: s.employeeName,
      deviceId: s.deviceId,
      deviceName: s.deviceName,
      standName: s.standName,
      standId: s.standId,
      bonusRate,
      weeklyTarget,
      startTime: s.startTime,
      endTime: s.endTime,
      isOpen: s.isOpen,
      quantity,
      unit: device.unit || '',
      weekKey,
      weekStart: weekStart.toISOString(),
      weekEnd: weekStart.endOf('isoWeek').toISOString(),
    };
  });

  // Drugi przebieg: suma produkcji (pracownik, maszyna, tydzień ISO) -> próg
  // premiowy tej sumy -> wartość dodatku, anotowana na każdej sesji tej grupy.
  const weeklyQuantityByGroup = {};
  withQuantity.forEach((s) => {
    const key = `${s.employeeId}|${s.deviceId}|${s.weekKey}`;
    weeklyQuantityByGroup[key] = (weeklyQuantityByGroup[key] || 0) + (s.quantity || 0);
  });

  return withQuantity.map((s) => {
    const key = `${s.employeeId}|${s.deviceId}|${s.weekKey}`;
    const weeklyQuantity = weeklyQuantityByGroup[key] || 0;
    const weeklyUnitsOverNorma = s.weeklyTarget != null ? Math.max(0, weeklyQuantity - s.weeklyTarget) : null;

    let weeklyValue = 0;
    if (weeklyUnitsOverNorma > 0 && s.bonusRate != null) {
      const { reached } = evaluateBonusThresholds(weeklyQuantity, thresholdsByDevice[s.deviceId] || []);
      if (reached) weeklyValue = weeklyUnitsOverNorma * s.bonusRate * reached.coefficient;
    }

    return { ...s, weeklyQuantity, weeklyUnitsOverNorma, weeklyValue };
  });
}

/**
 * Sesje na maszynach anotowane wynikiem swojego tygodnia (patrz
 * fetchAnnotatedSessions), przycięte do żądanego zakresu dat — zachowuje
 * dotychczasowy kontrakt (tablica sesji) dla istniejących konsumentów.
 */
export async function computeProductionSessions(supabase, { idConfiguration, dateFrom, dateTo, employeeId = null, deviceId = null }) {
  const all = await fetchAnnotatedSessions(supabase, { idConfiguration, dateFrom, dateTo, employeeId, deviceId });
  const from = dayjs(dateFrom).startOf('day');
  const to = dayjs(dateTo).endOf('day');
  return all.filter((s) => {
    const d = dayjs(s.startTime);
    return !d.isBefore(from) && !d.isAfter(to);
  });
}

/**
 * Dedupikuje zaanotowane sesje (patrz fetchAnnotatedSessions) do jednego wiersza
 * na (pracownik, maszyna, tydzień ISO) — to jest jednostka, w której istnieje
 * premia od wprowadzenia normy tygodniowej (nie per sesja/dzień).
 */
export function summarizeWeeklyBonuses(sessions) {
  const byGroup = {};
  sessions.forEach((s) => {
    const key = `${s.employeeId}|${s.deviceId}|${s.weekKey}`;
    if (!byGroup[key]) {
      byGroup[key] = {
        weekKey: s.weekKey,
        weekStart: s.weekStart,
        weekEnd: s.weekEnd,
        employeeId: s.employeeId,
        employeeName: s.employeeName,
        deviceId: s.deviceId,
        deviceName: s.deviceName,
        standName: s.standName,
        unit: s.unit,
        bonusRate: s.bonusRate,
        quantity: s.weeklyQuantity,
        weeklyTarget: s.weeklyTarget,
        unitsOverNorma: s.weeklyUnitsOverNorma,
        value: s.weeklyValue,
      };
    }
  });
  return Object.values(byGroup);
}

/**
 * Wiersze premii tygodniowej dla okresu, poprawnie przypisane do dokładnie
 * jednego okresu nawet gdy tydzień ISO przecina jego granicę: tydzień należy
 * do okresu, w którym wypada jego niedziela (weekEnd) — to zapobiega zarówno
 * podwójnemu liczeniu premii granicznego tygodnia (np. w dwóch sąsiednich
 * miesiącach Rozliczeń), jak i jej zgubieniu. Liczone z nieprzyciętych sesji
 * (fetchAnnotatedSessions), żeby tygodnie graniczne miały poprawną sumę nawet
 * gdy część ich sesji wypada poza `dateFrom`/`dateTo`.
 */
export async function computeWeeklyBonusesForPeriod(supabase, { idConfiguration, dateFrom, dateTo, employeeId = null, deviceId = null }) {
  const all = await fetchAnnotatedSessions(supabase, { idConfiguration, dateFrom, dateTo, employeeId, deviceId });
  const weekly = summarizeWeeklyBonuses(all);
  const from = dayjs(dateFrom).startOf('day');
  const to = dayjs(dateTo).endOf('day');
  return weekly.filter((w) => {
    const end = dayjs(w.weekEnd);
    return !end.isBefore(from) && !end.isAfter(to);
  });
}

/**
 * "Historia produkcji" jednego pracownika w okresie — cienka nakładka na
 * computeWeeklyBonusesForPeriod zawężająca wynik do jednego pracownika i
 * sumująca wartość dodatku. Zachowuje dotychczasowy kształt wywołania dla
 * istniejących konsumentów (np. PaymentCardComponent): `rows` są teraz wierszami
 * tygodniowymi (jeden na tydzień ISO), nie sesjami.
 */
export async function computeEmployeeProductionHistory(supabase, { employeeId, idConfiguration, dateFrom, dateTo }) {
  const rows = await computeWeeklyBonusesForPeriod(supabase, { idConfiguration, dateFrom, dateTo, employeeId });
  const totalValue = rows.reduce((sum, r) => sum + r.value, 0);
  return { rows, totalValue };
}
